// src/components/layout/Header.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Profile, useProfile } from "../Profile/ProfileContext";
import AccountSidebar from "../AccountSideBar/AccountSidebar";
import AuthModal from "../AccountSideBar/AuthModal";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAddressGuard } from "../../hooks/useAddressGuard";
import { useCart } from "../../contexts/CartContext";
import { useOrderNotifications } from "../../contexts/OrderNotificationsContext";
import LanguageSelector from "../ui/LanguageSelector";
import { fetchShirtsFromApi } from "../../services/shirtService";

// ─── Types ───────────────────────────────────────────────────────────────────

type Suggestion = {
  label: string;
  query: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function dedupe<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

function buildShirtSuggestions(
  shirts: { club: string | null; team: string | null; player: string | null; league: string | null }[],
  query: string,
): Suggestion[] {
  if (!query) return [];
  const q = query.toLowerCase();

  const clubs = dedupe(shirts.map((s) => s.club).filter(Boolean) as string[]);
  const teams = dedupe(shirts.map((s) => s.team).filter(Boolean) as string[]);
  const players = dedupe(shirts.map((s) => s.player).filter(Boolean) as string[]);
  const leagues = dedupe(shirts.map((s) => s.league).filter(Boolean) as string[]);

  const all = [...clubs, ...teams, ...players, ...leagues];
  const seen = new Set<string>();
  const results: Suggestion[] = [];

  for (const label of all) {
    const key = label.toLowerCase();
    if (key.includes(q) && !seen.has(key)) {
      seen.add(key);
      results.push({ label, query: label });
    }
    if (results.length >= 6) break;
  }
  return results;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Header() {
  const { t } = useTranslation();
  const { hasProfile, setProfile, profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { authModalOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const { isAuthed } = useAuth();
  const { guardAction, AddressModal } = useAddressGuard();
  const { cartCount } = useCart();
  const { actionCount: orderActionCount } = useOrderNotifications();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Autocomplete state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [allShirts, setAllShirts] = useState<{ club: string | null; team: string | null; player: string | null; league: string | null }[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Load shirts once for suggestions
  useEffect(() => {
    fetchShirtsFromApi()
      .then((shirts) =>
        setAllShirts(
          shirts.map((s) => ({
            club: s.club,
            team: s.team ?? null,
            player: s.player,
            league: s.league,
          })),
        ),
      )
      .catch(() => {});
  }, []);

  // Sync search field when navigating to catalog
  useEffect(() => {
    if (location.pathname !== "/catalog") return;
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get("q") ?? "");
  }, [location.pathname, location.search]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Debounced suggestion build
  const fetchSuggestions = useCallback(
    (q: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (!q.trim()) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      debounceTimer.current = setTimeout(() => {
        const suggs = buildShirtSuggestions(allShirts, q.trim());
        setSuggestions(suggs);
        setShowSuggestions(suggs.length > 0);
        setHighlightedIndex(-1);
      }, 150);
    },
    [allShirts],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    fetchSuggestions(val);
  };

  const selectSuggestion = (s: Suggestion) => {
    setShowSuggestions(false);
    setSearchQuery(s.query);
    const params =
      location.pathname === "/catalog"
        ? new URLSearchParams(location.search)
        : new URLSearchParams();
    params.set("q", s.query);
    navigate(`/catalog?${params.toString()}`);
    setMobileNavOpen(false);
  };

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setShowSuggestions(false);

    const trimmed = searchQuery.trim();
    const params =
      location.pathname === "/catalog"
        ? new URLSearchParams(location.search)
        : new URLSearchParams();

    if (trimmed) {
      params.set("q", trimmed);
    } else {
      params.delete("q");
    }

    const search = params.toString();
    navigate(`/catalog${search ? `?${search}` : ""}`);
    setMobileNavOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      selectSuggestion(suggestions[highlightedIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const onAccountClick = () =>
    hasProfile ? setOpenDrawer(true) : openAuthModal();

  return (
    <>
      <header className="site-header" role="banner">
        <div className="container header-inner">
          <a className="logo" href="/" aria-label="FSM Home">
            <img
              src="/images/FSM_logo.png"
              alt="FSM"
              className="logo__img"
              style={{
                height: "56px",
                width: "auto",
                display: "block",
                objectFit: "contain",
              }}
            />
          </a>

          <button
            type="button"
            className="nav-burger"
            aria-label={t("header.menu")}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            ☰
          </button>

          <form
            className="search"
            role="search"
            aria-label={t("common.search")}
            onSubmit={onSearchSubmit}
            autoComplete="off"
          >
            <label htmlFor="q" className="sr-only">
              {t("header.searchLabel")}
            </label>
            <div className="search-wrapper" ref={searchRef}>
              <input
                id="q"
                name="q"
                type="search"
                placeholder={t("header.searchPlaceholder")}
                value={searchQuery}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onFocus={() => {
                  if (suggestions.length > 0) setShowSuggestions(true);
                }}
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                aria-controls="search-suggestions"
              />
              <button
                type="submit"
                className="search-submit"
                aria-label={t("common.search")}
              >
                <img
                  className="search-icon"
                  src="/images/Magnifying_glass_icon.png"
                  alt=""
                />
              </button>

              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <ul
                  id="search-suggestions"
                  role="listbox"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid var(--border, #e5e7eb)",
                    borderRadius: "var(--radius, 6px)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    listStyle: "none",
                    margin: 0,
                    padding: "4px 0",
                    zIndex: 2000,
                    maxHeight: 320,
                    overflowY: "auto",
                  }}
                >
                  {suggestions.map((s, i) => (
                    <li
                      key={`shirt-${s.label}`}
                      role="option"
                      aria-selected={i === highlightedIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectSuggestion(s);
                      }}
                      onMouseEnter={() => setHighlightedIndex(i)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.6rem",
                        padding: "0.5rem 0.75rem",
                        cursor: "pointer",
                        background: i === highlightedIndex ? "var(--bg-subtle, #f3f4f6)" : "transparent",
                        fontSize: "0.9rem",
                      }}
                    >
                      <img
                        src="/images/Magnifying_glass_icon.png"
                        alt=""
                        style={{ width: 14, height: 14, opacity: 0.5, flexShrink: 0 }}
                      />
                      <span>{s.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </form>

          <nav
            className={`primary-nav ${mobileNavOpen ? "is-open" : ""}`}
            aria-label="Hoofdmenu"
          >
            <ul>
              <li>
                <button
                  type="button"
                  className="nav-icon"
                  aria-label={t("header.favorites")}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/favorites");
                  }}
                >
                  <img
                    src="/images/empty_heart_icon.svg"
                    alt={t("header.favorites")}
                  />
                </button>
              </li>


              <li>
                <button
                  type="button"
                  className="nav-icon"
                  aria-label={t("header.cart")}
                  onClick={() => {
                    setMobileNavOpen(false);
                    navigate("/cart");
                  }}
                  style={{ position: "relative" }}
                >
                  <img
                    src="/images/winkelmand_icon.svg"
                    alt={t("header.cart")}
                  />
                  {cartCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: "var(--brand-2, #f97316)",
                        border: "2px solid #fff",
                        display: "block",
                      }}
                    />
                  )}
                </button>
              </li>

              <li>
                <button
                  type="button"
                  className="nav-icon"
                  aria-label={
                    hasProfile
                      ? profile?.displayName || "Account"
                      : t("header.createAccount")
                  }
                  onClick={() => {
                    setMobileNavOpen(false);
                    onAccountClick();
                  }}
                  style={{ position: "relative" }}
                >
                  <img src="/images/account_icon.svg" alt="Account" />
                  {orderActionCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background: "var(--brand-2, #f97316)",
                        border: "2px solid #fff",
                        display: "block",
                      }}
                    />
                  )}
                </button>
              </li>

              <li className="nav-item nav-item--cta">
                <button
                  type="button"
                  className="cta"
                  onClick={() => {
                    setMobileNavOpen(false);
                    if (!isAuthed) {
                      openAuthModal();
                    } else {
                      guardAction(() => navigate("/sell"));
                    }
                  }}
                >
                  {t("header.sellShirt")}
                </button>
              </li>

              <li className="nav-item nav-item--lang">
                <LanguageSelector />
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <AccountSidebar
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onOpenAuthModal={openAuthModal}
      />
      <AuthModal
        open={authModalOpen}
        onClose={closeAuthModal}
        onCreate={(p: Profile) => {
          setProfile(p);
          closeAuthModal();
        }}
      />
      {AddressModal}
    </>
  );
}
